import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";

// Context Providers
import { AuthProvider, LanguageProvider, useAuth } from "@/lib/context";
import { ProAuthProvider } from "@/lib/ProAuthContext";
import { PrefsProvider } from "@/lib/usePrefs";

// Source Tracking
import { SourceTracker } from "@/components/SourceTracker";

// Pages
// Route "/" uses HomePageV3 exclusively (canonical white homepage).
// Legacy ServiceMVPLanding has been deleted; MagicBar lives in
// /components/MagicBar.jsx.
import { ServiceRequestDetailPage, BrowseRequestsPage } from "@/pages/ServiceMVP";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import ForgotPasswordPage from "@/pages/ForgotPassword";
import ResetPasswordPage from "@/pages/ResetPassword";
import { JobsPage, JobDetailPage } from "@/pages/Jobs";
import CreateJobWizardPage from "@/pages/jobs/CreateJobWizard";
import CreateJobSuccessPage from "@/pages/jobs/CreateJobSuccess";
import { 
  CompanyDashboard, 
  CreateJobPage, 
  CompanyJobsPage, 
  JobApplicationsPage,
  CompanyProfilePage 
} from "@/pages/CompanyDashboard";
import { 
  WorkerDashboard, 
  CapabilityCardPage, 
  WorkerApplicationsPage 
} from "@/pages/WorkerDashboard";
import { 
  ToolRentalsPage, 
  AutoRepairPage, 
  ServiceRequestsPage 
} from "@/pages/Marketplace";
import { 
  AdminDashboard, 
  AdminUsersPage, 
  AdminPaymentsPage,
  AdminAuditLogsPage
} from "@/pages/Admin";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import { NotificationsPage } from "@/pages/Notifications";
import { CVEnhancePage } from "@/pages/CVEnhance";
import { 
  SubcontractingPage, 
  SubcontractingDetailPage, 
  SubcontractingCreatePage 
} from "@/pages/Subcontracting";
import { AutoServicesPage, AutoServiceDetailPage } from "@/pages/AutoServices";
import { ServiceRequestPage, MyRequestsPage, RequestDetailPage } from "@/pages/ServiceRequest";
import { EquipmentRentals, EquipmentDetail, CreateEquipment } from "@/pages/EquipmentRentals";
import VehicleRentalRequestPage from "@/pages/VehicleRentalRequestPage";
import { LogisticsLandingPage, PostLoadPage, OfferCapacityPage, FindCarrierPage, BackhaulPage } from "@/pages/LogisticsPage";
import CompanyProfilePublicPage from "@/pages/CompanyProfilePublic";
import PricingPage from "@/pages/PricingPage";
import WorkAbilityCardPage from "@/pages/WorkAbilityCardPage";
import AlertSettingsPage from "@/pages/AlertSettingsPage";
import GlobalServiceRequestPage from "@/pages/GlobalServiceRequest";
import ProviderPreferencesPage from "@/pages/ProviderPreferencesPage";
import SettingsPage from "@/pages/SettingsPage";
import { LanguageSuggestionBanner } from "@/components/LanguageSuggestionBanner";
import InvestorPage from "@/pages/InvestorPage";
import LegalPage from "@/pages/LegalPage";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileLanguageFAB from "@/components/MobileLanguageFAB";
import QuickRequestFAB from "@/components/QuickRequestFAB";
import BuildStamp from "@/components/BuildStamp";
import BuildFreshnessCheck from "@/components/BuildFreshnessCheck";
import "@/styles/mobile-first.css";

// PRO Authentication Pages
import ProLoginPage from "@/pages/ProLoginPage";
import ProDashboardPage from "@/pages/ProDashboardPage";
import { ProSuccessPage, ProCancelPage } from "@/pages/ProPaymentPages";
import ProAuthCallback from "@/components/ProAuthCallback";

// Universal Booking System
import BookingPage from "@/pages/BookingPage";
import BookingStatusPage from "@/pages/BookingStatusPage";
import { ProviderResponsePage } from "@/pages/booking/ProviderResponsePage";
import TransportPage from "@/pages/TransportPage";
import TransportBookingPage from "@/pages/TransportBookingPage";
import TransportRequestPage from "@/pages/TransportRequestPage";
import AutoRepairBookingPage from "@/pages/AutoRepairBookingPage";
import EquipmentBookingPage from "@/pages/EquipmentBookingPage";
import SubcontractingBookingPage from "@/pages/SubcontractingBookingPage";

// SMS Wallet Pages
import { WalletSuccessPage, WalletCancelPage } from "@/pages/WalletPages";

// Provider System
import ProviderRegisterPage from "@/pages/provider/ProviderRegisterPage";
import ProviderLoginPage from "@/pages/provider/ProviderLoginPage";
import ProviderDashboardPage from "@/pages/provider/ProviderDashboardPage";
import ProviderMapVisibilityPage from "@/pages/provider/ProviderMapVisibilityPage";

// Platform Admin Dashboard
import PlatformAdminDashboard from "@/pages/admin/PlatformAdminDashboard";
import HomePageV3 from "@/pages/HomePageV3";
import CVBuilderPage from "@/pages/CVBuilderPage";
import DemoSeedRedirect from "@/pages/DemoSeedRedirect";
import LiveDemoPage from "@/pages/LiveDemoPage";
import AdminP1Page from "@/pages/admin/AdminP1Page";

// Admin App (Separate Auth System)
import AdminApp from "@/admin/AdminApp";

// Marketplace - Hanked & Alltöövõtt
import { MarketplaceHomePage, HankedPage, AlltooovottPage } from "@/pages/marketplace";

// SEO Pages
import AutoRepairSeoPage from "@/pages/seo/AutoRepairSeoPage";
import JobsSeoPage from "@/pages/seo/JobsSeoPage";
import ServicesSeoPage from "@/pages/seo/ServicesSeoPage";

// Accommodation Module
import {
  AccommodationBrowsePage,
  AccommodationListingPage,
  AccommodationBookingPage,
  HostDashboardPage,
  CreateListingPage
} from "@/pages/accommodation";
import RentalsSeoPage from "@/pages/seo/RentalsSeoPage";

// Live Map
import { LiveMapPage, ProviderProfilePage } from "@/pages/map";

// Business Catalog
import { BusinessCatalogPage, BusinessDetailPage } from "@/pages/business";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-brand border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Dashboard Redirect - redirects to role-specific dashboard
const DashboardRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-brand border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  if (user.role === 'company') {
    return <Navigate to="/company/dashboard" replace />;
  }
  
  return <Navigate to="/worker/dashboard" replace />;
};

// Logistics aliases — forward to canonical /logistics(/post-load) while
// preserving any query string the user/share-link arrived with.
const LogisticsAliasRedirect = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ''}`} replace />;
};

// Main App Component
function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - Service MVP is now the homepage */}
      <Route path="/" element={<HomePageV3 />} />
      <Route path="/demo" element={<DemoSeedRedirect />} />
      <Route path="/live" element={<LiveDemoPage />} />
      <Route path="/cv-builder" element={<CVBuilderPage />} />
      <Route path="/cv" element={<CVBuilderPage />} />
      
      {/* Live Map - Public */}
      <Route path="/map" element={<LiveMapPage />} />
      <Route path="/services/map" element={<LiveMapPage />} />
      <Route path="/provider/:id" element={<ProviderProfilePage />} />
      
      {/* Business Catalog - Public */}
      <Route path="/businesses" element={<BusinessCatalogPage />} />
      <Route path="/business/:id" element={<BusinessDetailPage />} />
      
      {/* Service MVP Routes */}
      <Route path="/service-request/:id" element={<ServiceRequestDetailPage />} />
      <Route path="/browse-requests" element={<BrowseRequestsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/signup" element={<RegisterPage />} /> {/* Alias for /register */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Route Aliases for cleaner URLs */}
      <Route path="/ai-cv" element={<CVEnhancePage />} /> {/* Alias for CV builder */}

      {/*
        Pakun tööd — new canonical job creation flow.
        Deliberately NOT wrapped in ProtectedRoute: the wizard component handles
        its own auth gating (logged-out → /login?next=; worker/private → inline
        company mini-form). This prevents the map-flash bug caused by the old
        ProtectedRoute bouncing non-company users to "/" (homepage with map).
      */}
      <Route path="/jobs/create" element={<CreateJobWizardPage />} />
      <Route path="/jobs/create/success" element={<CreateJobSuccessPage />} />
      {/* Legacy aliases → canonical wizard */}
      <Route path="/jobs/new" element={<Navigate to="/jobs/create" replace />} />
      <Route path="/dashboard" element={<DashboardRedirect />} /> {/* Role-based redirect */}
      
      {/* PRO Authentication & Subscription Routes */}
      <Route path="/pro/login" element={<ProLoginPage />} />
      <Route path="/pro/callback" element={<ProAuthCallback />} />
      <Route path="/pro/dashboard" element={<ProDashboardPage />} />
      <Route path="/pro/success" element={<ProSuccessPage />} />
      <Route path="/pro/cancel" element={<ProCancelPage />} />
      
      {/* SMS Wallet Routes */}
      <Route path="/wallet/success" element={<WalletSuccessPage />} />
      <Route path="/wallet/cancel" element={<WalletCancelPage />} />
      
      {/* Provider System Routes */}
      <Route path="/provider/register" element={<ProviderRegisterPage />} />
      <Route path="/provider/login" element={<ProviderLoginPage />} />
      <Route path="/provider/dashboard" element={<ProviderDashboardPage />} />
      <Route path="/provider/map-visibility" element={<ProviderMapVisibilityPage />} />
      <Route path="/become-provider" element={<ProviderRegisterPage />} />
      
      {/* Platform Admin Dashboard */}
      <Route path="/platform-admin" element={<PlatformAdminDashboard />} />
      
      {/* SEO Pages - Jobs by Country/Category (MUST come before /jobs/:id) */}
      <Route path="/jobs/finland" element={<JobsSeoPage />} />
      <Route path="/jobs/finland/:category" element={<JobsSeoPage />} />
      <Route path="/jobs/estonia" element={<JobsSeoPage />} />
      <Route path="/jobs/estonia/:category" element={<JobsSeoPage />} />
      <Route path="/jobs/sweden" element={<JobsSeoPage />} />
      <Route path="/jobs/sweden/:category" element={<JobsSeoPage />} />
      <Route path="/jobs/norway" element={<JobsSeoPage />} />
      <Route path="/jobs/norway/:category" element={<JobsSeoPage />} />
      <Route path="/jobs/germany" element={<JobsSeoPage />} />
      <Route path="/jobs/germany/:category" element={<JobsSeoPage />} />
      <Route path="/jobs/denmark" element={<JobsSeoPage />} />
      <Route path="/jobs/denmark/:category" element={<JobsSeoPage />} />
      <Route path="/tood/:country" element={<JobsSeoPage />} />
      <Route path="/tood/:country/:category" element={<JobsSeoPage />} />
      <Route path="/tyot/:country" element={<JobsSeoPage />} />
      <Route path="/tyot/:country/:category" element={<JobsSeoPage />} />
      
      {/* Job Marketplace (Public) */}
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/jobs/:id" element={<JobDetailPage />} />
      
      {/* Marketplace Pages (Public) */}
      <Route path="/tools" element={<ToolRentalsPage />} />
      <Route path="/auto-repair" element={<AutoRepairPage />} />
      <Route path="/accommodation" element={<AccommodationBrowsePage />} />
      <Route path="/accommodation/:listingId" element={<AccommodationListingPage />} />
      <Route path="/accommodation/booking/:bookingId" element={<AccommodationBookingPage />} />
      
      {/* Estonian URL aliases for accommodation */}
      <Route path="/majutus" element={<AccommodationBrowsePage />} />
      <Route path="/majutus/otsi" element={<AccommodationBrowsePage />} />
      <Route path="/majutus/paku" element={<HostDashboardPage />} />
      <Route path="/services" element={<ServiceRequestsPage />} />
      <Route path="/cv-enhance" element={<CVEnhancePage />} />
      <Route path="/cv" element={<CVEnhancePage />} />
      <Route path="/ai-cv" element={<CVEnhancePage />} />
      {/* Host Dashboard Routes */}
      <Route path="/host/dashboard" element={<HostDashboardPage />} />
      <Route path="/host/listings/new" element={<CreateListingPage />} />
      <Route path="/host/listings/:listingId/edit" element={<CreateListingPage />} />
      
      {/* Category Route Aliases - Global Nav */}
      <Route path="/equipment" element={<EquipmentRentals />} />
      <Route path="/transport" element={<TransportPage />} />
      
      {/* Sõidulehed / full Transport Request flow */}
      <Route path="/transport/request" element={<TransportRequestPage />} />
      <Route path="/soidulehed" element={<TransportRequestPage />} />
      
      {/* Transport Deep-link Routes - Direct booking with preselected subcategory */}
      <Route path="/transport/:subcategory" element={<TransportBookingPage />} />
      
      {/* Auto Repair Deep-link Routes */}
      <Route path="/auto-repair/:subcategory" element={<AutoRepairBookingPage />} />
      
      {/* Equipment/Rentals Deep-link Routes */}
      <Route path="/equipment/:subcategory" element={<EquipmentBookingPage />} />
      
      {/* Subcontracting Deep-link Routes */}
      <Route path="/alltooovott/:subcategory" element={<SubcontractingBookingPage />} />
      
      {/* Alltöövõtt Estonian URL aliases for seek/offer modes */}
      <Route path="/alltooovott/otsi" element={<SubcontractingBookingPage />} />
      <Route path="/alltooovott/paku" element={<Navigate to="/provider/register?category=subcontracting" replace />} />
      
      {/* Equipment Rentals Module */}
      <Route path="/rentals" element={<EquipmentRentals />} />
      <Route path="/rentals/vehicle/request" element={<VehicleRentalRequestPage />} />

      {/* Logistics MVP — Loads + Capacity (Backhaul / Load Board light) */}
      <Route path="/logistics" element={<LogisticsLandingPage />} />
      <Route path="/logistics/post-load" element={<PostLoadPage />} />
      <Route path="/logistics/offer-capacity" element={<OfferCapacityPage />} />
      <Route path="/logistics/find-carrier" element={<FindCarrierPage />} />
      <Route path="/logistics/backhaul" element={<BackhaulPage />} />

      {/* Logistics Hub aliases — preserve query params (?demo, ?country, ?lang) */}
      <Route path="/logistics-hub" element={<LogisticsAliasRedirect to="/logistics" />} />
      <Route path="/transport/logistics" element={<LogisticsAliasRedirect to="/logistics" />} />
      <Route path="/post-load" element={<LogisticsAliasRedirect to="/logistics/post-load" />} />
      <Route path="/postita-veos" element={<LogisticsAliasRedirect to="/logistics/post-load" />} />
      
      {/* SEO Pages - Rentals by Country/Category */}
      <Route path="/rentals/finland" element={<RentalsSeoPage />} />
      <Route path="/rentals/finland/:category" element={<RentalsSeoPage />} />
      <Route path="/rentals/estonia" element={<RentalsSeoPage />} />
      <Route path="/rentals/estonia/:category" element={<RentalsSeoPage />} />
      <Route path="/rentals/sweden" element={<RentalsSeoPage />} />
      <Route path="/rentals/sweden/:category" element={<RentalsSeoPage />} />
      <Route path="/rentals/norway" element={<RentalsSeoPage />} />
      <Route path="/rentals/norway/:category" element={<RentalsSeoPage />} />
      <Route path="/rendid/:country" element={<RentalsSeoPage />} />
      <Route path="/rendid/:country/:category" element={<RentalsSeoPage />} />
      <Route path="/vuokraus/:country" element={<RentalsSeoPage />} />
      <Route path="/vuokraus/:country/:category" element={<RentalsSeoPage />} />
      
      <Route path="/rentals/new" element={
        <ProtectedRoute allowedRoles={['worker', 'company']}>
          <CreateEquipment />
        </ProtectedRoute>
      } />
      <Route path="/rentals/:id" element={<EquipmentDetail />} />
      
      {/* Auto Services Module */}
      <Route path="/auto" element={<AutoServicesPage />} />
      <Route path="/auto/:id" element={<AutoServiceDetailPage />} />
      
      {/* SEO Pages - Auto Repair by City */}
      <Route path="/autoremont/:city" element={<AutoRepairSeoPage />} />
      <Route path="/autoremont/:city/:service" element={<AutoRepairSeoPage />} />
      <Route path="/autokorjaamo/:city" element={<AutoRepairSeoPage />} />
      <Route path="/autokorjaamo/:city/:service" element={<AutoRepairSeoPage />} />
      <Route path="/auto-repair/:city" element={<AutoRepairSeoPage />} />
      <Route path="/auto-repair/:city/:service" element={<AutoRepairSeoPage />} />
      
      {/* SEO Pages - Services by Country/City/Type */}
      <Route path="/services/finland" element={<ServicesSeoPage />} />
      <Route path="/services/finland/:city" element={<ServicesSeoPage />} />
      <Route path="/services/finland/:city/:serviceType" element={<ServicesSeoPage />} />
      <Route path="/services/estonia" element={<ServicesSeoPage />} />
      <Route path="/services/estonia/:city" element={<ServicesSeoPage />} />
      <Route path="/services/estonia/:city/:serviceType" element={<ServicesSeoPage />} />
      <Route path="/services/sweden" element={<ServicesSeoPage />} />
      <Route path="/services/sweden/:city" element={<ServicesSeoPage />} />
      <Route path="/services/sweden/:city/:serviceType" element={<ServicesSeoPage />} />
      <Route path="/services/norway" element={<ServicesSeoPage />} />
      <Route path="/services/norway/:city" element={<ServicesSeoPage />} />
      <Route path="/services/norway/:city/:serviceType" element={<ServicesSeoPage />} />
      <Route path="/teenused/:country" element={<ServicesSeoPage />} />
      <Route path="/teenused/:country/:city" element={<ServicesSeoPage />} />
      <Route path="/teenused/:country/:city/:serviceType" element={<ServicesSeoPage />} />
      <Route path="/palvelut/:country" element={<ServicesSeoPage />} />
      <Route path="/palvelut/:country/:city" element={<ServicesSeoPage />} />
      <Route path="/palvelut/:country/:city/:serviceType" element={<ServicesSeoPage />} />
      
      {/* Workshop Service Requests */}
      <Route path="/services/request" element={<ServiceRequestPage />} />
      <Route path="/dashboard/requests" element={<MyRequestsPage />} />
      <Route path="/dashboard/requests/:requestId" element={<RequestDetailPage />} />
      
      {/* Payment Pages */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/payments/success" element={<PaymentSuccessPage />} />
      <Route path="/payments/cancel" element={<PaymentCancelPage />} />
      
      {/* Universal Booking System */}
      <Route path="/booking" element={<BookingPage />} />
      <Route path="/booking/status/:bookingId" element={<BookingStatusPage />} />
      <Route path="/booking/respond/:bookingId" element={<ProviderResponsePage />} />
      
      {/* Worker Routes */}
      <Route path="/worker/dashboard" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/worker/capability-card" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <CapabilityCardPage />
        </ProtectedRoute>
      } />
      <Route path="/worker/applications" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <WorkerApplicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/worker/notifications" element={
        <ProtectedRoute allowedRoles={['worker']}>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      
      {/* Company Routes */}
      <Route path="/company/dashboard" element={
        <ProtectedRoute allowedRoles={['company']}>
          <CompanyDashboard />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs" element={
        <ProtectedRoute allowedRoles={['company']}>
          <CompanyJobsPage />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/new" element={<Navigate to="/jobs/create" replace />} />
      <Route path="/company/jobs/:id/applications" element={
        <ProtectedRoute allowedRoles={['company']}>
          <JobApplicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/applicants" element={
        <ProtectedRoute allowedRoles={['company']}>
          <CompanyJobsPage />
        </ProtectedRoute>
      } /> {/* Alias for company jobs/applicants */}
      <Route path="/company/profile" element={
        <ProtectedRoute allowedRoles={['company']}>
          <CompanyProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/p1" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminP1Page />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUsersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminPaymentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-logs" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAuditLogsPage />
        </ProtectedRoute>
      } />
      
      {/* Subcontracting / Hanke Routes */}
      <Route path="/subcontracting" element={<SubcontractingPage />} />
      <Route path="/subcontracting/create" element={
        <ProtectedRoute allowedRoles={['company']}>
          <SubcontractingCreatePage />
        </ProtectedRoute>
      } />
      <Route path="/subcontracting/:jobId" element={<SubcontractingDetailPage />} />
      
      {/* Unified Marketplace - Hanked & Alltöövõtt (Separate top-level routes) */}
      <Route path="/hanked" element={<HankedPage />} />
      <Route path="/alltooovott" element={<AlltooovottPage />} />
      <Route path="/marketplace" element={<MarketplaceHomePage />} />
      
      {/* Public Company Profile */}
      <Route path="/company/:slug" element={<CompanyProfilePublicPage />} />
      
      {/* Pricing / Subscription Plans */}
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Work Ability Card */}
      <Route path="/work-ability-card" element={
        <ProtectedRoute>
          <WorkAbilityCardPage />
        </ProtectedRoute>
      } />
      
      {/* Alert Settings */}
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/settings/alerts" element={
        <ProtectedRoute>
          <AlertSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <AlertSettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Global Service Request Engine */}
      <Route path="/service-requests" element={<GlobalServiceRequestPage />} />
      <Route path="/service-requests/:id" element={<GlobalServiceRequestPage />} />
      
      {/* Provider Service Preferences */}
      <Route path="/settings/provider-preferences" element={
        <ProtectedRoute>
          <ProviderPreferencesPage />
        </ProtectedRoute>
      } />
      
      {/* Investor Page - English Only */}
      <Route path="/invest" element={<InvestorPage />} />
      <Route path="/investors" element={<InvestorPage />} />
      
      {/* Legal Pages - Jurisdiction Aware */}
      <Route path="/legal/:documentType" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      
      {/* Secure Admin Area - Separate Auth System */}
      <Route path="/secure-admin/*" element={<AdminApp />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <HelmetProvider>
        <LanguageProvider>
          <AuthProvider>
            <ProAuthProvider>
              <PrefsProvider>
                <BrowserRouter>
                  {/* Auto-detect traffic source for conversion attribution */}
                  <SourceTracker />
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                  {/* Language auto-detection suggestion banner */}
                  <LanguageSuggestionBanner />
                  {/* Mobile Navigation */}
                  <MobileBottomNav />
                  {/* Mobile Language Switcher - 1 tap access */}
                  <MobileLanguageFAB />
                  {/* Quick Request FAB - frequency capped */}
                  <QuickRequestFAB />
                  {/* Build / Environment Stamp (always visible) */}
                  <BuildStamp />
                  {/* One-shot hard reload if a fresher build is deployed. */}
                  <BuildFreshnessCheck />
                </BrowserRouter>
              </PrefsProvider>
            </ProAuthProvider>
          </AuthProvider>
        </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
